import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronRight, CheckCircle2, Shield, Package, Settings, Truck, Megaphone, Award, Target, BarChart3, RefreshCw, Zap, Users, TrendingUp, FileText, Eye, ShoppingCart, Headphones } from 'lucide-react';

const SwiggyInstamartAccountManagement = () => {
  const serviceInclusions = [
    {
      icon: Shield,
      title: 'Brand Onboarding & Approval',
      description: 'End-to-end assistance with Swiggy Instamart onboarding.',
      features: [
        'Complete support for brand and product approval as per Instamart policies',
        'Documentation support and coordination with Swiggy\'s category and onboarding teams'
      ]
    },
    {
      icon: Package,
      title: 'Product Listing & SEO Optimization',
      description: 'Creation of keyword-rich, SEO-optimized listings aligned with Instamart\'s search algorithm.',
      features: [
        'Optimized product titles, descriptions, and attributes to maximize visibility',
        'Ensuring listing accuracy to avoid suppression, rejection, or delisting'
      ]
    },
    {
      icon: Truck,
      title: 'Inventory & Supply Management',
      description: 'Support with inventory onboarding and smooth inwarding to Instamart warehouses.',
      features: [
        'Regular stock monitoring to minimize out-of-stock scenarios',
        'Demand-based inventory planning to maintain consistent availability and sales momentum'
      ]
    },
    {
      icon: Megaphone,
      title: 'Targeted Advertising & Promotions',
      description: 'Setup and management of Swiggy Instamart ad campaigns.',
      features: [
        'Continuous optimization to achieve higher ROI and controlled acquisition costs',
        'Participation in Instamart promotions, offers, and seasonal campaigns to boost sales'
      ]
    }
  ];

  const scopeOfWork = [
    'Brand onboarding and approvals',
    'Product listing and SEO optimization',
    'Inventory coordination',
    'Advertising and promotion management',
    'Ongoing account health and performance monitoring'
  ];

  const benefits = [
    { icon: Shield, title: 'Onboarding Included', description: 'We manage the complete Swiggy Instamart onboarding process from start to finish.' },
    { icon: Settings, title: 'End-to-End Account Management', description: 'From listings to inventory, ads, and promotions — we handle everything.' },
    { icon: Zap, title: 'Quick Commerce Expertise', description: 'Strong experience in managing Instamart and other instant commerce platforms.' },
    { icon: Eye, title: 'SEO-Driven Listings', description: 'Optimized listings that improve discoverability and conversions on Instamart.' },
    { icon: TrendingUp, title: 'Performance-Focused Advertising', description: 'Data-backed ad strategies designed to deliver measurable ROI.' },
    { icon: Headphones, title: 'Dedicated Support', description: 'A dedicated account manager for proactive communication and execution.' },
    { icon: Award, title: 'Proven E-commerce Experience', description: 'Cross-platform expertise enabling scalable and sustainable growth.' }
  ];

  const deliverables = [
    'Successfully onboarded brand and products on Swiggy Instamart',
    'SEO-optimized product listings with enhanced discoverability',
    'Managed inventory levels to minimize stock-outs and maintain sales momentum',
    'Executed advertising and promotional campaigns aimed at improving ROI',
    'Ongoing reports and insights on account performance and health'
  ];

  const instamartFees = [
    'Swiggy Instamart follows a selective product onboarding model with a listing fee per SKU',
    'The fee structure includes provisions for advertising credits for running promotional and advertising campaigns',
    'A portion of the fee is charged as Swiggy platform and listing fees'
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Swiggy Instamart Account Management Services | Obiquiste Solutions</title>
        <meta name="description" content="Professional Swiggy Instamart account management: brand onboarding, SEO-optimized listings, inventory management, advertising & promotions. Grow your quick commerce business with experts." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/swiggy-instamart-account-management" />
      </Helmet>
      
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center text-sm text-slate-600">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/services" className="hover:text-primary">Services</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-orange-500 font-medium">Swiggy Instamart Account Management</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <BarChart3 className="w-4 h-4 mr-2" />
                Quick Commerce Management
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Swiggy Instamart <span className="text-orange-500">Account Management</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                Obiquiste Solutions offers specialized Swiggy Instamart Account Management Services designed to help brands grow on one of India's leading instant commerce platforms. With Swiggy Instamart's fast-paced delivery ecosystem and high customer demand, strategic positioning is crucial for achieving maximum visibility, sales, and profitability.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?audit=true">
                  <Button className="bg-orange-500 text-white hover:bg-orange-600 px-8 py-6 rounded-full text-base font-semibold">
                    Get Started
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Services Ensure */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-center mb-8">
            Our Services Ensure
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, text: 'Seamless onboarding and brand approval on Swiggy Instamart' },
              { icon: Eye, text: 'SEO-optimized product listings for higher discoverability' },
              { icon: Package, text: 'Efficient inventory and supply planning for Instamart warehouses' },
              { icon: TrendingUp, text: 'Improved ROI through data-driven advertising and promotions' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 p-4 bg-orange-50 rounded-xl"
              >
                <item.icon className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                <p className="text-slate-700 text-sm">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Inclusions */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-4">
            Our Service Inclusions
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-3xl mx-auto">
            We focus on end-to-end account management to ensure your Instamart operations run smoothly and profitably.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {serviceInclusions.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <service.icon className="w-7 h-7 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold mb-2">{service.title}</h3>
                    <p className="text-slate-600 text-sm">{service.description}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-sm font-medium text-slate-700 mb-3">Features:</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Scope of Work */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Scope of Work
              </h2>
              <p className="text-slate-600 mb-8">
                Our comprehensive Swiggy Instamart account management services encompass the following areas:
              </p>
              <ul className="space-y-4">
                {scopeOfWork.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-slate-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-8 text-white"
            >
              <h3 className="font-heading text-2xl font-bold mb-6">Deliverables</h3>
              <ul className="space-y-4">
                {deliverables.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-200 mt-0.5 flex-shrink-0" />
                    <span className="text-orange-50">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-4">
            Benefits of Partnering with Obiquiste Solutions
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-3xl mx-auto">
            Our expert team brings strong experience in quick commerce operations and Instamart-specific strategies to ensure smooth execution and sustainable growth.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {benefits.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-12 bg-orange-50 border-y border-orange-100">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-heading text-xl font-bold mb-4 text-orange-800">
              Important Note on Swiggy Instamart Fees
            </h3>
            <ul className="space-y-3">
              {instamartFees.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-orange-500 to-red-500">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Grow on Swiggy Instamart?
          </h2>
          <p className="text-orange-100 text-lg mb-4 max-w-2xl mx-auto">
            With the right strategy and execution, Swiggy Instamart can become a powerful growth channel for your brand in the instant commerce space.
          </p>
          <p className="text-white text-lg mb-8 max-w-2xl mx-auto font-medium">
            Obiquiste Solutions is committed to delivering enhanced visibility, higher conversions, and consistent revenue growth on Instamart.
          </p>
          <Link to="/contact?audit=true">
            <Button className="bg-white text-orange-600 hover:bg-slate-100 px-8 py-6 rounded-full text-base font-semibold">
              Get Free Consultation
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SwiggyInstamartAccountManagement;
